import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_WORK_ORDER_TIME_ENTRIES,
  GET_TOTAL_TIME_WORKED,
  GET_TIME_SUMMARY_BY_USER,
  ADD_MANUAL_TIME_ENTRY,
  UPDATE_TIME_ENTRY,
  DELETE_TIME_ENTRY,
} from '../../graphql/work-order-time';
import { GET_WORK_ORDER } from '../../graphql/queries';
import { format } from 'date-fns';
import { TimeEntryGPSInfo } from './gps';

/**
 * Format minutes into "X hours Y minutes" format
 */
const formatDuration = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return '0 minutes';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  if (minutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

/**
 * WorkOrderTimeTracker - Simple manual time logging for work orders
 * No live timer - just add time entries manually to track work performed
 */
const WorkOrderTimeTracker = ({
  workOrderId,
  workOrderStatus,
  currentUser,
  property,
  onQuickComplete,
  isQuickCompleting = false,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Get property location if not passed as prop
  const { data: workOrderData } = useQuery(GET_WORK_ORDER, {
    variables: { id: workOrderId },
    skip: !workOrderId || !!property,
  });

  const propertyLocation = property?.latitude && property?.longitude
    ? { latitude: property.latitude, longitude: property.longitude }
    : workOrderData?.workOrder?.property?.latitude && workOrderData?.workOrder?.property?.longitude
    ? { latitude: workOrderData.workOrder.property.latitude, longitude: workOrderData.workOrder.property.longitude }
    : null;

  // Queries
  const { data: totalTimeData, refetch: refetchTotalTime } = useQuery(GET_TOTAL_TIME_WORKED, {
    variables: { workOrderId },
    fetchPolicy: 'network-only',
  });

  const { data: timeEntriesData, refetch: refetchTimeEntries } = useQuery(GET_WORK_ORDER_TIME_ENTRIES, {
    variables: { workOrderId },
    fetchPolicy: 'network-only',
  });

  const { data: timeSummaryData, refetch: refetchTimeSummary } = useQuery(GET_TIME_SUMMARY_BY_USER, {
    variables: { workOrderId },
    fetchPolicy: 'network-only',
  });

  // Mutations
  const [addManualTimeEntry, { loading: addingTime }] = useMutation(ADD_MANUAL_TIME_ENTRY, {
    onCompleted: () => {
      toast.success('Time entry added');
      setIsAddModalOpen(false);
      refetchAll();
    },
    onError: (error) => toast.error(`Failed to add time: ${error.message}`),
  });

  const [updateTimeEntry, { loading: updatingTime }] = useMutation(UPDATE_TIME_ENTRY, {
    onCompleted: () => {
      toast.success('Time entry updated');
      setIsEditModalOpen(false);
      setEditingEntry(null);
      refetchAll();
    },
    onError: (error) => toast.error(`Failed to update time: ${error.message}`),
  });

  const [deleteTimeEntry] = useMutation(DELETE_TIME_ENTRY, {
    onCompleted: () => {
      toast.success('Time entry deleted');
      refetchAll();
    },
    onError: (error) => toast.error(`Failed to delete time: ${error.message}`),
  });

  const refetchAll = useCallback(() => {
    refetchTotalTime();
    refetchTimeEntries();
    refetchTimeSummary();
  }, [refetchTotalTime, refetchTimeEntries, refetchTimeSummary]);

  // Refetch time data when work order status changes (e.g. to COMPLETED)
  // so the display reflects final totals after the backend stops active entries
  const prevStatusRef = useRef(workOrderStatus);
  useEffect(() => {
    if (prevStatusRef.current !== workOrderStatus) {
      prevStatusRef.current = workOrderStatus;
      // Small delay to let the backend finish stopping active time entries
      const timer = setTimeout(() => refetchAll(), 500);
      return () => clearTimeout(timer);
    }
  }, [workOrderStatus, refetchAll]);

  const totalTimeWorked = totalTimeData?.totalTimeWorked || 0;
  const timeEntries = timeEntriesData?.workOrderTimeEntries || [];
  const timeSummary = timeSummaryData?.timeSummaryByUser || [];
  const showQuickComplete = (
    typeof onQuickComplete === 'function' &&
    workOrderStatus !== 'COMPLETED' &&
    workOrderStatus !== 'CANCELED'
  );

  const handleEditEntry = (entry) => {
    setEditingEntry({
      ...entry,
      hours: Math.floor((entry.durationMinutes || 0) / 60),
      minutes: (entry.durationMinutes || 0) % 60,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingEntry) return;
    
    const totalMinutes = (parseInt(editingEntry.hours) || 0) * 60 + (parseInt(editingEntry.minutes) || 0);
    
    updateTimeEntry({
      variables: {
        input: {
          timeEntryId: editingEntry.id,
          durationMinutes: totalMinutes,
        },
      },
    });
  };

  const handleDeleteEntry = (entryId) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      deleteTimeEntry({
        variables: { timeEntryId: entryId },
      });
    }
  };

  const handleAddTime = (data) => {
    addManualTimeEntry({
      variables: {
        input: {
          workOrderId,
          durationMinutes: data.hours * 60 + data.minutes,
        },
      },
    });
  };

  // Don't show time tracker for canceled work orders
  if (workOrderStatus === 'CANCELED') {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Main Time Display Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-blue-100">
              <svg 
                className="h-5 w-5 text-blue-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Time Worked</h3>
              <p className="text-lg font-semibold text-gray-900">
                {formatDuration(totalTimeWorked)}
              </p>
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {showQuickComplete && (
              <button
                type="button"
                onClick={onQuickComplete}
                disabled={isQuickCompleting}
                className="inline-flex w-full items-center justify-center rounded-md border border-green-600 bg-white px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isQuickCompleting ? (
                  <>
                    <svg className="mr-1 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Completing...
                  </>
                ) : (
                  <>
                    <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Quick Complete
                  </>
                )}
              </button>
            )}

            {/* Add Time Button */}
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex w-full items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Log Time
            </button>

            {/* Toggle Details */}
            {timeEntries.length > 0 && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="inline-flex items-center px-2 py-2 text-sm text-gray-500 hover:text-gray-700"
                title={showDetails ? 'Hide details' : 'Show details'}
              >
                <svg 
                  className={`h-5 w-5 transform transition-transform ${showDetails ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details Section */}
      {showDetails && (
        <div className="px-4 py-3 space-y-4">
          {/* Time by User Summary */}
          {timeSummary.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Time by Team Member</h4>
              <div className="space-y-1">
                {timeSummary.map((summary) => (
                  <div key={summary.userId} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{summary.userName}</span>
                    <span className="font-medium text-gray-900">{formatDuration(summary.totalMinutes)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Time Entries List */}
          {timeEntries.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Time Entries</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {timeEntries.map((entry) => (
                  <div 
                    key={entry.id} 
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {entry.user?.firstName} {entry.user?.lastName}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a')}
                      </div>
                      {/* GPS Information */}
                      <TimeEntryGPSInfo timeEntry={entry} propertyLocation={propertyLocation} />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {formatDuration(entry.durationMinutes)}
                      </span>
                      <button
                        onClick={() => handleEditEntry(entry)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="Edit time"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete time"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {timeEntries.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No time entries yet. Click "Log Time" to add your first entry.</p>
          )}
        </div>
      )}

      {/* Edit Time Modal */}
      {isEditModalOpen && editingEntry && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsEditModalOpen(false)} />
            
            <div className="relative inline-block bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-sm sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Time Entry</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                      <input
                        type="number"
                        min="0"
                        value={editingEntry.hours}
                        onChange={(e) => setEditingEntry({ ...editingEntry, hours: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={editingEntry.minutes}
                        onChange={(e) => setEditingEntry({ ...editingEntry, minutes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500">
                    Total: {formatDuration((parseInt(editingEntry.hours) || 0) * 60 + (parseInt(editingEntry.minutes) || 0))}
                  </p>
                </div>
              </div>
              
              <div className="mt-5 sm:mt-6 flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingEntry(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={updatingTime}
                  className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {updatingTime ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Time Modal */}
      {isAddModalOpen && (
        <AddTimeModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddTime}
          loading={addingTime}
        />
      )}
    </div>
  );
};

/**
 * Modal for adding a time entry
 */
const AddTimeModal = ({ onClose, onSubmit, loading }) => {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);

  const handleSubmit = () => {
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    
    if (h === 0 && m === 0) {
      toast.error('Please enter a time duration');
      return;
    }
    onSubmit({ hours: h, minutes: m });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative inline-block bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-sm sm:w-full sm:p-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Log Time Worked</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                  <input
                    type="number"
                    min="0"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <p className="text-sm text-gray-500">
                Total: {formatDuration(parseInt(hours || 0) * 60 + parseInt(minutes || 0))}
              </p>
            </div>
          </div>
          
          <div className="mt-5 sm:mt-6 flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Log Time'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderTimeTracker;
